import { create } from "zustand";
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { getConfig } from "@/config";
import { ROLE_PERMISSIONS, type Permission } from "@/utils/permissions";

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: Permission[];
}

type LoginResult =
  | { requiresMfa: true; session: string; mfaType: "cognito" | "app" }
  | { requiresNewPassword: true; requiredAttributes: string[] }
  | { requiresMfa: false };

/** F-FORCE-PWD — first-login challenge state. Cognito returns
 *  `NEW_PASSWORD_REQUIRED` after `admin_create_user`; the user has to
 *  set a permanent password before the session is issued. We stash the
 *  Session token + email so the SetNewPassword page can complete the
 *  RespondToAuthChallenge round-trip without re-prompting for the
 *  temp password. `requiredAttributes` is whatever Cognito ships in
 *  ChallengeParameters.requiredAttributes (usually empty for our pool). */
interface NewPasswordChallenge {
  session: string;
  email: string;
  requiredAttributes: string[];
}

interface AuthState {
  user: AdminUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaSession: string | null;
  cognitoUser: CognitoUser | null;
  newPasswordChallenge: NewPasswordChallenge | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (code: string) => Promise<void>;
  completeNewPasswordChallenge: (
    newPassword: string,
    attributes?: Record<string, string>,
  ) => Promise<LoginResult>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  setUser: (user: AdminUser, token: string) => void;
  initSession: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
}

function getUserPool(): CognitoUserPool {
  const config = getConfig();
  return new CognitoUserPool({
    UserPoolId: config.cognitoPoolId,
    ClientId: config.cognitoClientId,
  });
}

/**
 * Did this refresh fail because the SESSION is invalid, or because the
 * request could not complete (MPPI-00842)?
 *
 * The distinction decides whether we destroy the stored session, and
 * `signOut()` clears Cognito's localStorage for the whole ORIGIN — every
 * tab, not just this one. So treating a network blip as an invalid
 * session signs the person out everywhere, and the tab they are looking
 * at keeps working off its in-memory token and still appears signed in.
 * The next tab they open finds nothing and lands on /login, which is
 * exactly the symptom the operator reported after clicking a link in the
 * daily digest.
 *
 * Cognito names the real cases. Anything else — a dropped connection, a
 * DNS hiccup, a 5xx from the token endpoint — must leave the session
 * alone and let the caller fail.
 */
export function isSessionInvalid(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? "";
  const message = (err as { message?: string } | null)?.message ?? "";
  const text = `${name} ${message}`;
  return (
    /NotAuthorizedException/i.test(text) ||
    /UserNotFoundException/i.test(text) ||
    /PasswordResetRequiredException/i.test(text) ||
    /UserNotConfirmedException/i.test(text) ||
    /Refresh Token has (been revoked|expired)/i.test(text) ||
    /Invalid Refresh Token/i.test(text) ||
    // `refreshToken()` throws this itself when the pool holds no user at
    // all, which IS an absent session rather than a failed request.
    /No current user/i.test(text) ||
    /No session/i.test(text)
  );
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(base64));
}

// F-018 — proactive refresh timer. Cognito access tokens are valid
// for an hour; if we sit on a stale token until the next fetch hits
// 401, the user sees a brief blank-panel flash before the retry. By
// scheduling a refresh ~5 minutes before `exp`, active users never
// hit the 401 round-trip in the first place.
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const REFRESH_LEAD_SECONDS = 300; // 5 min before exp

function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(token: string, refresh: () => Promise<void>): void {
  clearRefreshTimer();
  try {
    const payload = decodeJwtPayload(token);
    const exp = typeof payload.exp === "number" ? payload.exp : null;
    if (!exp) return;
    const nowSec = Math.floor(Date.now() / 1000);
    const fireInMs = Math.max(10, exp - nowSec - REFRESH_LEAD_SECONDS) * 1000;
    refreshTimer = setTimeout(() => {
      refresh().catch(() => {
        // Best-effort — adminFetch's 401 path will catch real expiry.
      });
    }, fireInMs);
  } catch {
    // Bad token shape; let the next fetch's 401 path handle recovery.
  }
}

/**
 * Re-check the token whenever the tab comes back to the foreground
 * (MPPI-00797).
 *
 * The proactive timer above is correct and insufficient. Browsers THROTTLE
 * `setTimeout` in a hidden tab — Chrome drops a backgrounded page to roughly
 * one timer callback a minute, and after five minutes hidden applies
 * intensive throttling on a budget. A timer armed for 55 minutes' time
 * therefore fires late, or not until the tab is looked at again. The token
 * expires meanwhile and the operator returns to a login screen.
 *
 * The 401 path in `adminFetch` is the safety net and it does work, but only
 * once a request has already failed — so the first thing the returning user
 * sees is a failure, which is exactly what the timer existed to avoid.
 *
 * So on `visibilitychange` and on `focus`: if the token is inside the
 * refresh window or already past it, refresh now; otherwise re-arm the
 * timer, because a throttled timer's remaining delay cannot be trusted.
 */
let focusWatcherAttached = false;

/**
 * Propagate a SIGN-OUT to every other tab (MPPI-00879).
 *
 * Rehydration already works both ways: a new tab reads Cognito's
 * localStorage, and idle time is shared so one tab's activity keeps them
 * all alive (MPPI-00846). The asymmetry was the ending. When one tab
 * signed out — or had its session revoked — `signOut()` cleared Cognito's
 * localStorage for the whole origin, but every OTHER tab kept its token in
 * MEMORY and went on looking signed in until its next request failed. The
 * person had a logged-out browser showing logged-in screens.
 *
 * `storage` fires in the other tabs, never in the one that wrote, which is
 * exactly the semantics needed here. It fires for the Cognito SDK's own
 * keys, so the test is "did the current user disappear", asked of the SDK
 * rather than by matching key names — the SDK's key format is its business
 * and has changed before.
 */
let signOutWatcherAttached = false;

function attachCrossTabSignOut(onSignedOut: () => void): void {
  if (signOutWatcherAttached || typeof window === "undefined") return;
  signOutWatcherAttached = true;

  window.addEventListener("storage", (e: StorageEvent) => {
    // A full localStorage.clear() reports key === null. Both shapes matter:
    // Cognito removes individual keys, a hard reset wipes everything.
    if (e.key !== null && !e.key.startsWith("CognitoIdentityServiceProvider")) {
      return;
    }
    try {
      if (!getUserPool().getCurrentUser()) onSignedOut();
    } catch {
      // A blocked or unavailable store must not throw inside an event
      // handler; the 401 path is still the net of last resort.
    }
  });
}

function attachFocusRefresh(
  getToken: () => string | null,
  refresh: () => Promise<void>,
): void {
  if (focusWatcherAttached || typeof document === "undefined") return;
  focusWatcherAttached = true;

  const check = (): void => {
    if (document.visibilityState !== "visible") return;
    const token = getToken();
    if (!token) return;
    try {
      const payload = decodeJwtPayload(token);
      const exp = typeof payload.exp === "number" ? payload.exp : null;
      if (!exp) return;
      const remaining = exp - Math.floor(Date.now() / 1000);
      if (remaining <= REFRESH_LEAD_SECONDS) {
        refresh().catch(() => {
          // adminFetch's 401 path remains the net of last resort.
        });
      } else {
        // The tab was hidden; whatever delay the timer has left is a
        // throttled guess. Re-arm it from the token's real expiry.
        scheduleProactiveRefresh(token, refresh);
      }
    } catch {
      // Bad token shape; the next request's 401 path handles it.
    }
  };

  document.addEventListener("visibilitychange", check);
  window.addEventListener("focus", check);
}

function extractUser(session: CognitoUserSession): AdminUser {
  const idToken = session.getIdToken().getJwtToken();
  const payload = decodeJwtPayload(idToken);

  const role = ((payload["custom:admin_role"] as string) || "analyst").toLowerCase();
  const permissions = ROLE_PERMISSIONS[role] || [];

  return {
    id: payload.sub as string,
    email: payload.email as string,
    name: (payload.name as string) || (payload.email as string),
    role,
    permissions,
  };
}

/** Shared post-auth flow used by both the password login and the
 *  NEW_PASSWORD_REQUIRED completion path. Builds a CognitoUserSession
 *  from the tokens, sets state, then checks app-level OTP. */
async function finaliseAuthenticatedSession(
  authResult: {
    IdToken: string;
    AccessToken: string;
    RefreshToken: string;
  },
  cognitoUser: CognitoUser,
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
): Promise<LoginResult> {
  const { CognitoIdToken, CognitoAccessToken, CognitoRefreshToken } =
    await import("amazon-cognito-identity-js");
  const idToken = new CognitoIdToken({ IdToken: authResult.IdToken });
  const accessToken = new CognitoAccessToken({ AccessToken: authResult.AccessToken });
  const refreshToken = new CognitoRefreshToken({ RefreshToken: authResult.RefreshToken });
  const session = new CognitoUserSession({ IdToken: idToken, AccessToken: accessToken, RefreshToken: refreshToken });

  cognitoUser.setSignInUserSession(session);

  const user = extractUser(session);
  const token = session.getIdToken().getJwtToken();

  set({
    user,
    accessToken: token,
    isLoading: false,
    cognitoUser,
    newPasswordChallenge: null,
  });
  scheduleProactiveRefresh(token, () => get().refreshToken());
  attachFocusRefresh(() => get().accessToken, () => get().refreshToken());
  attachCrossTabSignOut(() => {
    set({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.href = "/login?reason=signed-out";
    }
  });

  try {
    const { fetchMfaStatusOnce } = await import("@/api/otp");
    const status = await fetchMfaStatusOnce();
    if (status?.mfa_required) {
      set({ mfaSession: "APP_OTP" });
      return { requiresMfa: true, session: "", mfaType: "app" };
    }
  } catch {
    // If the status check fails (e.g. network), proceed as if no MFA is
    // required. Each subsequent admin request will independently re-gate.
  }

  set({ isAuthenticated: true });
  return { requiresMfa: false };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  mfaSession: null,
  cognitoUser: null,
  newPasswordChallenge: null,

  login: async (email, password) => {
    const config = getConfig();
    const userPool = getUserPool();
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

    // Use USER_PASSWORD_AUTH flow (non-SRP) via direct Cognito API call
    const response = await fetch(
      `https://cognito-idp.${config.cognitoRegion}.amazonaws.com/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
        },
        body: JSON.stringify({
          AuthFlow: "USER_PASSWORD_AUTH",
          ClientId: config.cognitoClientId,
          AuthParameters: {
            USERNAME: email,
            PASSWORD: password,
          },
        }),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.__type || "Authentication failed");
    }

    // F-FORCE-PWD — admin-created Cognito users land here on first
    // login. We stash the Session token (single-use, ~3min TTL) and
    // any required-attribute names so the SetNewPassword screen can
    // complete the challenge without re-prompting for the temp pw.
    if (data.ChallengeName === "NEW_PASSWORD_REQUIRED") {
      let requiredAttributes: string[] = [];
      try {
        const raw = data.ChallengeParameters?.requiredAttributes;
        if (typeof raw === "string") {
          // Cognito ships this as a JSON-encoded string of attribute
          // names like ["userAttributes.given_name"]. Strip the
          // "userAttributes." prefix for display.
          const parsed = JSON.parse(raw) as string[];
          requiredAttributes = parsed.map((a) =>
            a.startsWith("userAttributes.") ? a.slice("userAttributes.".length) : a,
          );
        }
      } catch {
        // If parsing fails, fall through with an empty list — the
        // SetNewPassword form just won't render the attributes block.
      }
      set({
        cognitoUser,
        newPasswordChallenge: {
          session: data.Session,
          email,
          requiredAttributes,
        },
      });
      return { requiresNewPassword: true, requiredAttributes };
    }

    if (data.ChallengeName) {
      // Cognito-side MFA challenge (SMS_MFA / SOFTWARE_TOKEN_MFA). Not in
      // use right now because UAE numbers can't reach Cognito SMS, but
      // kept in case a future user_pool flips MFA back on.
      set({ cognitoUser, mfaSession: data.ChallengeName });
      return { requiresMfa: true, session: data.Session, mfaType: "cognito" };
    }

    return await finaliseAuthenticatedSession(
      data.AuthenticationResult,
      cognitoUser,
      set,
      get,
    );
  },

  completeNewPasswordChallenge: async (newPassword, attributes) => {
    const config = getConfig();
    const { newPasswordChallenge, cognitoUser } = get();
    if (!newPasswordChallenge || !cognitoUser) {
      throw new Error(
        "No active password-change session — please sign in again.",
      );
    }

    // Cognito requires every attribute name to be prefixed with
    // "userAttributes." in the ChallengeResponses payload.
    const challengeResponses: Record<string, string> = {
      USERNAME: newPasswordChallenge.email,
      NEW_PASSWORD: newPassword,
    };
    if (attributes) {
      for (const [k, v] of Object.entries(attributes)) {
        if (!v) continue;
        challengeResponses[`userAttributes.${k}`] = v;
      }
    }

    const response = await fetch(
      `https://cognito-idp.${config.cognitoRegion}.amazonaws.com/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target":
            "AWSCognitoIdentityProviderService.RespondToAuthChallenge",
        },
        body: JSON.stringify({
          ChallengeName: "NEW_PASSWORD_REQUIRED",
          ClientId: config.cognitoClientId,
          Session: newPasswordChallenge.session,
          ChallengeResponses: challengeResponses,
        }),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      // Re-throw with a friendlier message. Common failure modes:
      // - InvalidPasswordException: doesn't meet pool's policy
      // - NotAuthorizedException: Session expired (>3min idle on form)
      throw new Error(data.message || data.__type || "Couldn't set new password");
    }

    // Cognito CAN return another ChallengeName here (e.g. MFA_SETUP)
    // if the pool requires post-password setup, but our pool doesn't
    // — we expect AuthenticationResult straight away.
    if (!data.AuthenticationResult) {
      throw new Error(
        `Unexpected challenge after new-password: ${data.ChallengeName ?? "unknown"}`,
      );
    }

    return await finaliseAuthenticatedSession(
      data.AuthenticationResult,
      cognitoUser,
      set,
      get,
    );
  },

  verifyMfa: async (code) => {
    const { cognitoUser, mfaSession } = get();

    // App-level OTP path: hit our backend; on success, just flip the
    // authenticated bit (the JWT was already issued at the Cognito step).
    if (mfaSession === "APP_OTP") {
      const { adminFetch } = await import("@/api/client");
      try {
        await adminFetch("/api/v1/auth/otp/verify", {
          method: "POST",
          body: JSON.stringify({ code }),
        });
      } catch (err) {
        throw new Error(err instanceof Error ? err.message : "Verification failed");
      }
      set({ isAuthenticated: true, isLoading: false, mfaSession: null });
      return;
    }

    // Cognito-side MFA path (SMS_MFA / SOFTWARE_TOKEN_MFA). Currently
    // unreachable in production (pool MFA is OFF), but kept for
    // forward-compat.
    if (!cognitoUser) throw new Error("No active login session");
    const challengeType: "SMS_MFA" | "SOFTWARE_TOKEN_MFA" =
      mfaSession === "SOFTWARE_TOKEN_MFA"
        ? "SOFTWARE_TOKEN_MFA"
        : "SMS_MFA";

    return new Promise<void>((resolve, reject) => {
      cognitoUser.sendMFACode(
        code,
        {
          onSuccess: (session) => {
            const user = extractUser(session);
            const token = session.getIdToken().getJwtToken();
            set({
              user,
              accessToken: token,
              isAuthenticated: true,
              isLoading: false,
              mfaSession: null,
            });
            scheduleProactiveRefresh(token, () => get().refreshToken());
            attachFocusRefresh(() => get().accessToken, () => get().refreshToken());
  attachFocusRefresh(() => get().accessToken, () => get().refreshToken());
            resolve();
          },
          onFailure: (err) => {
            reject(new Error(err.message || "MFA verification failed"));
          },
        },
        challengeType,
      );
    });
  },

  logout: () => {
    clearRefreshTimer();
    const userPool = getUserPool();
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      mfaSession: null,
      cognitoUser: null,
      newPasswordChallenge: null,
    });
  },

  refreshToken: async () => {
    const userPool = getUserPool();
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) throw new Error("No current user");

    return new Promise<void>((resolve, reject) => {
      currentUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session) {
            reject(err || new Error("No session"));
            return;
          }
          const user = extractUser(session);
          const token = session.getIdToken().getJwtToken();
          set({ user, accessToken: token, isAuthenticated: true, isLoading: false });
          scheduleProactiveRefresh(token, () => get().refreshToken());
  attachFocusRefresh(() => get().accessToken, () => get().refreshToken());
          resolve();
        },
      );
    });
  },

  setUser: (user, token) => {
    set({ user, accessToken: token, isAuthenticated: true, isLoading: false });
    scheduleProactiveRefresh(token, () => get().refreshToken());
  attachFocusRefresh(() => get().accessToken, () => get().refreshToken());
  },

  initSession: async () => {
    try {
      await get().refreshToken();
    } catch (err) {
      // Only wipe when the session is genuinely invalid (MPPI-00842). A
      // failed REQUEST must not destroy a good session — doing so clears
      // localStorage for every tab on this origin.
      if (!isSessionInvalid(err)) {
        set({ isLoading: false });
        return;
      }
      // Stale / revoked session — wipe Cognito SDK localStorage and
      // store state together so the next render lands cleanly on
      // /login instead of half-rendering a stale dashboard. Common
      // trigger: a SUPER_ADMIN ran admin-set-user-password while the
      // user's tab still had old tokens.
      try {
        const pool = getUserPool();
        pool.getCurrentUser()?.signOut();
      } catch {
        // SDK throws if pool is unconfigured — best-effort cleanup.
      }
      set({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        mfaSession: null,
        cognitoUser: null,
        newPasswordChallenge: null,
      });
    }
  },

  hasPermission: (permission) => {
    const { user } = get();
    if (!user) return false;
    return user.permissions.includes(permission);
  },
}));
